/**
 * listコマンドハンドラ
 *
 * リソース（datasets, tabs, models, reports）の一覧を取得
 *
 * 使用例:
 * x:list {"resource":"datasets"}
 * x:list {"resource":"tabs"}
 * x:list {"resource":"models"}
 * x:list {"resource":"reports"}
 */

import type { CommandHandler, CommandResult, ParsedCommand, CommandContext } from '../types';
import logger from '../../utils/logger';

/**
 * x:listコマンドで受け付けるJSON形式
 */
interface ListInput {
  resource: 'datasets' | 'tabs' | 'models' | 'reports';
}

export const listHandler: CommandHandler = {
  name: 'list',
  description: 'List resources (datasets, tabs, models, reports)',
  category: 'utility',
  parameters: [
    {
      name: 'json',
      type: 'string',
      required: false,
      description: 'JSON object with resource type to list'
    }
  ],
  aliases: [],
  examples: [
    'x:list {"resource":"datasets"}',
    'x:list {"resource":"tabs"}',
    'x:list {"resource":"models"}',
    'x:list {"resource":"reports"}'
  ],

  execute: async (args: ParsedCommand, context: CommandContext): Promise<CommandResult> => {
    const jsonStr = args.positionalArgs.join(' ').trim();

    if (!jsonStr) {
      return {
        success: false,
        message: 'Resource type required',
        error: {
          code: 'NO_JSON',
          message: 'No resource type specified',
          suggestion: 'Provide resource type: x:list {"resource":"datasets"}'
        }
      };
    }

    let input: ListInput;
    try {
      input = JSON.parse(jsonStr) as ListInput;
    } catch (e) {
      return {
        success: false,
        message: 'Invalid JSON format',
        error: {
          code: 'INVALID_JSON',
          message: `Failed to parse JSON: ${e instanceof Error ? e.message : String(e)}`,
          suggestion: 'Check JSON syntax'
        }
      };
    }

    if (!input.resource) {
      return {
        success: false,
        message: 'Resource type required',
        error: {
          code: 'NO_RESOURCE',
          message: 'No resource type specified',
          suggestion: 'Valid resources: datasets, tabs, models, reports'
        }
      };
    }

    switch (input.resource) {
      case 'datasets': {
        const datasets = Object.entries(context.datasets).map(([id, ds]) => ({
          id,
          name: ds.name,
          rows: ds.rowCount ?? 0,
          columns: ds.schema.columns.length,
          type: ds.type,
          ...(ds.type === 'derived' ? { parentIds: ds.parentDatasetIds } : {})
        }));
        logger.info('[AgentCommand] List datasets', datasets);
        return {
          success: true,
          message: `Found ${datasets.length} dataset(s)`,
          data: datasets
        };
      }

      case 'tabs': {
        const tabs = context.getTabs();
        logger.info('[AgentCommand] List tabs', tabs);
        return {
          success: true,
          message: `Found ${tabs.length} tab(s)`,
          data: tabs
        };
      }

      case 'models': {
        const models = context.getModels().map(m => ({
          id: m.id,
          type: m.type,
          name: m.name,
          datasetId: m.metadata.trainingDatasetId,
          ...(m.type === 'glm' && 'family' in m.parameters ? { family: m.parameters.family } : {})
        }));
        logger.info('[AgentCommand] List models', models);
        return {
          success: true,
          message: `Found ${models.length} model(s)`,
          data: models
        };
      }

      case 'reports': {
        const reports = context.getReports().map(r => ({
          id: r.id,
          name: r.name,
          elementCount: r.elements.length
        }));
        logger.info('[AgentCommand] List reports', reports);
        return {
          success: true,
          message: `Found ${reports.length} report(s)`,
          data: reports
        };
      }

      default:
        return {
          success: false,
          message: `Unknown resource type: ${input.resource}`,
          error: {
            code: 'INVALID_RESOURCE',
            message: `Unknown resource type: ${input.resource}`,
            suggestion: 'Valid resources: datasets, tabs, models, reports'
          }
        };
    }
  }
};
